import Api from '@/services/Api';

function getCompanyCode(rootState, rootGetters) {
  return (
    (
      rootGetters['user/selectedCompanyCode'] ||
      rootState.contact.company
    )
      ?.toString?.()
      .trim?.() || null
  );
}

function getContactCode(rootGetters) {
  const userProfile = rootGetters['user/userProfile'] || {};
  return userProfile.code?.toString?.().trim?.() || null;
}

function parseApiResponse(xhr) {
  if (!xhr || !xhr.response) return null;
  return JSON.parse(xhr.response);
}

function normalizeTemplatesList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

export default {
  namespaced: true,
  state: {
    samplingTemplates: [],
    samplingTemplate: null,
    templateCreationDraft: null,
    defaultSamplingTemplate: null,
    samplingTemplatesFilters: {
      active: true,
      is_default: undefined,
      name: '',
    },
    loadingSamplingTemplates: false,
    loadingSamplingTemplate: false,
    loadingDefaultSamplingTemplate: false,
  },
  actions: {
    async fetchTemplates(
      { commit, state, rootState, rootGetters },
      filters = {}
    ) {
      try {
        commit('setLoadingSamplingTemplates', true);
        const mergedFilters = {
          ...state.samplingTemplatesFilters,
          ...filters,
        };
        const xhr = await Api.getSamplingTemplates(
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters),
          mergedFilters
        );
        const response = parseApiResponse(xhr);
        const templates = normalizeTemplatesList(response);
        commit('setSamplingTemplates', templates);
        commit('setSamplingTemplatesFilters', mergedFilters);
        return Promise.resolve(templates);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setLoadingSamplingTemplates', false);
      }
    },
    async fetchTemplate({ commit, rootState, rootGetters }, id) {
      try {
        commit('setLoadingSamplingTemplate', true);
        const xhr = await Api.getSamplingTemplateById(
          id,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setLoadingSamplingTemplate', false);
      }
    },
    async createTemplate(
      { commit, rootState, rootGetters },
      payload
    ) {
      try {
        const xhr = await Api.createSamplingTemplate(
          payload,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async updateTemplate(
      { commit, rootState, rootGetters },
      { id, data }
    ) {
      try {
        const xhr = await Api.updateSamplingTemplate(
          id,
          data,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deleteTemplate({ commit, rootState, rootGetters }, id) {
      try {
        const xhr = await Api.deleteSamplingTemplate(
          id,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setSamplingTemplate', null);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchDefaultTemplate({ commit, rootState, rootGetters }) {
      try {
        commit('setLoadingDefaultSamplingTemplate', true);
        const xhr = await Api.getDefaultSamplingTemplate(
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setDefaultSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        if (error?.status === 404) {
          commit('setDefaultSamplingTemplate', null);
          return Promise.resolve(null);
        }
        return Promise.reject(error);
      } finally {
        commit('setLoadingDefaultSamplingTemplate', false);
      }
    },
    async setTemplateAsDefault(
      { commit, rootState, rootGetters },
      id
    ) {
      try {
        const xhr = await Api.setDefaultSamplingTemplate(
          id,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setDefaultSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deactivateTemplate({ commit, rootState, rootGetters }, id) {
      try {
        const xhr = await Api.deactivateSamplingTemplate(
          id,
          getCompanyCode(rootState, rootGetters),
          getContactCode(rootGetters)
        );
        const response = parseApiResponse(xhr);
        commit('setSamplingTemplate', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setSamplingTemplates({ commit }, value) {
      commit('setSamplingTemplates', value);
    },
    setSamplingTemplate({ commit }, value) {
      commit('setSamplingTemplate', value);
    },
    setTemplateCreationDraft({ commit }, value) {
      commit('setTemplateCreationDraft', value);
    },
    setDefaultSamplingTemplate({ commit }, value) {
      commit('setDefaultSamplingTemplate', value);
    },
    setSamplingTemplatesFilters({ commit }, value) {
      commit('setSamplingTemplatesFilters', value);
    },
    setLoadingSamplingTemplates({ commit }, value) {
      commit('setLoadingSamplingTemplates', value);
    },
    setLoadingSamplingTemplate({ commit }, value) {
      commit('setLoadingSamplingTemplate', value);
    },
    setLoadingDefaultSamplingTemplate({ commit }, value) {
      commit('setLoadingDefaultSamplingTemplate', value);
    },
    resetData({ commit }) {
      commit('resetData');
    },
  },
  mutations: {
    setSamplingTemplates(state, value) {
      state.samplingTemplates = value;
    },
    setSamplingTemplate(state, value) {
      state.samplingTemplate = value;
    },
    setTemplateCreationDraft(state, value) {
      state.templateCreationDraft = value;
    },
    setDefaultSamplingTemplate(state, value) {
      state.defaultSamplingTemplate = value;
    },
    setSamplingTemplatesFilters(state, value) {
      state.samplingTemplatesFilters = value;
    },
    setLoadingSamplingTemplates(state, value) {
      state.loadingSamplingTemplates = value;
    },
    setLoadingSamplingTemplate(state, value) {
      state.loadingSamplingTemplate = value;
    },
    setLoadingDefaultSamplingTemplate(state, value) {
      state.loadingDefaultSamplingTemplate = value;
    },
    resetData(state) {
      state.samplingTemplates = [];
      state.samplingTemplate = null;
      state.templateCreationDraft = null;
      state.defaultSamplingTemplate = null;
      state.samplingTemplatesFilters = {
        active: true,
        is_default: undefined,
        name: '',
      };
      state.loadingSamplingTemplates = false;
      state.loadingSamplingTemplate = false;
      state.loadingDefaultSamplingTemplate = false;
    },
  },
};
